import clsx from 'clsx';
import React, { ReactNode, useState, useCallback } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import StackView from './StackView';

export interface DropdownMenuOption {
  label: string;
  value: any;
  disabled?: boolean;
}

interface DropdownControlProps {
  onClick: () => void;
}

interface DropdownMenuProps {
  children: (props: DropdownControlProps) => ReactNode;
  options: DropdownMenuOption[];
  value: any;
  onChange?: (value: any) => void;
  className?: string;
}

const DropdownMenu = ({
  children,
  options,
  value,
  onChange,
  className,
}: DropdownMenuProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleOutsideClick = useCallback(
    () => setIsMenuOpen(false),
    [setIsMenuOpen]
  );

  const handleOptionClick = useCallback(
    (option: DropdownMenuOption) => () => {
      if (onChange) onChange(option.value);
      setIsMenuOpen(false);
    },
    [onChange, setIsMenuOpen]
  );

  return (
    <OutsideClickHandler
      disabled={!isMenuOpen}
      onOutsideClick={handleOutsideClick}
    >
      <div className="relative">
        {children({ onClick: () => setIsMenuOpen((prev) => !prev) })}
        {isMenuOpen && (
          <StackView
            data-testid="dropdown-menu-container"
            direction="vertical"
            spacing={2}
            className={clsx(
              'absolute z-dropdown mt-1 p-2 bg-black text-white rounded-medium shadow-default',
              className
            )}
          >
            {options.map((option, i) => (
              <button
                data-testid={`dropdown-menu-option-${i}-${option.value}`}
                key={option.value}
                className={clsx('w-full text-left', {
                  'font-semibold': option.value === value,
                  'cursor-default opacity-40': option.disabled,
                })}
                onClick={handleOptionClick(option)}
                disabled={option.disabled}
              >
                {option.label}
              </button>
            ))}
          </StackView>
        )}
      </div>
    </OutsideClickHandler>
  );
};

export default DropdownMenu;
